import React, { useEffect, useState } from 'react'

import { useQuery } from '@apollo/client'
import { Box, Button, Form, Radio, Text } from '@factorialco/deprecated-design-system'
import { RateConfigurationsDatatableDocument } from 'generated/resources/datatable.graphql'
import {
  CreateRateConfigurationDocument,
  LegalEntityForSelectFragment,
  RateConfigurationAssigneesDataDocument,
  RateConfigurationFormFragment,
  UpdateRateConfigurationDocument,
} from 'generated/resources/graphql'

import FieldSet from 'modules/core/components/FieldSet'
import { LegalEntitySelector } from 'modules/core/components/LegalEntitySelector'
import Modal from 'modules/core/components/Modal'
import NumberField from 'modules/core/components/NumberField'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { fromCents, toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import { ExpensableRateConfigurationResourceTypeEnum } from 'modules/expenses/types/Expensable'

interface FormValues {
  name: string
  currency: string
  amountCents: number | null | undefined
  legalEntities: LegalEntityForSelectFragment[] | undefined
  strategy: string | undefined
  rate: number | undefined
}

const AccessGroupOptions = {
  manualSelection: 'manualSelection',
  legalEntities: 'legalEntities',
}

interface Props {
  title: string
  description: string
  rateConfiguration?: RateConfigurationFormFragment
  onClose: () => void
  resourceType: string
}

const trackSuccessEvent = (eventName: string, data: RateConfigurationFormFragment) => {
  const eventData = {
    rateConfigurationId: data.id,
    rateConfigurationName: data.name,
    rateConfigurationCurrency: data.currency,
    rateConfigurationAmount: data.amountCents,
    rateConfigurationLegalEntities: JSON.stringify(
      data.legalEntitiesConnection.nodes.map((x) => ({
        id: x.id,
        name: x.legalName,
        currency: x.currency,
      }))
    ),
  }
  track(eventName, eventData)
}

const RateFormModal = ({ title, description, rateConfiguration, onClose, resourceType }: Props) => {
  const { company } = useCurrentSession()
  const validCurrencies = company.legalEntities.map((le) => le.currency).filter(Boolean)
  const uniqueCurrencies = [...new Set(validCurrencies)]
  const currencyOptions = uniqueCurrencies.map((currency) => ({
    value: currency,
    label: currency,
  }))

  const [createRateConfiguration] = useMutation(CreateRateConfigurationDocument, {
    extractorFn: (data) => data.expensesMutations.createExpensesRateConfiguration,
    refetchQueries: [RateConfigurationsDatatableDocument],
  })
  const [updateRateConfiguration] = useMutation(UpdateRateConfigurationDocument, {
    extractorFn: (data) => data.expensesMutations.updateExpensesRateConfiguration,
    refetchQueries: [RateConfigurationsDatatableDocument],
  })

  let defaultStrategy = rateConfiguration?.strategy || ''

  if (rateConfiguration?.strategy === 'legal_entities') {
    defaultStrategy = AccessGroupOptions.legalEntities
  }

  if (rateConfiguration?.strategy === 'manual_selection') {
    defaultStrategy = AccessGroupOptions.manualSelection
  }

  const [strategyOption, setStrategyOption] = React.useState(defaultStrategy)

  const [employeeSelection, setEmployeeSelection] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: [],
  })

  const { loading: loadingEmployeesData, data: employeesData } = useQuery(
    RateConfigurationAssigneesDataDocument,
    {
      variables: {
        accessIds: rateConfiguration?.accessIds ?? [],
      },
    }
  )

  const clearSelection = () => {
    form.setFieldValue('legalEntities', [], false)
    setEmployeeSelection({ type: 'manual', ids: [] })
  }

  const form = useForm<FormValues>({
    validateOnChange: false,
    defaultValues: {
      name: rateConfiguration?.name || '',
      currency: rateConfiguration?.currency || '',
      amountCents: rateConfiguration?.amountCents
        ? fromCents(rateConfiguration?.amountCents)
        : null,
      rate: rateConfiguration?.rate || undefined,
      legalEntities: rateConfiguration?.legalEntitiesConnection?.nodes ?? [],
      strategy: rateConfiguration?.strategy || '',
    },
    validate: (data) => {
      const errors: FormErrors<FormValues> = {}

      if (!data.currency) {
        errors.currency = i18n.t('common.required')
      }
      if (
        !data.amountCents &&
        resourceType === ExpensableRateConfigurationResourceTypeEnum.PerDiem
      ) {
        errors.amountCents = i18n.t('common.required')
      }
      if (!data.rate && resourceType === ExpensableRateConfigurationResourceTypeEnum.Mileage) {
        errors.rate = i18n.t('common.required')
      }
      if (!data.name) {
        errors.name = i18n.t('common.required')
      }

      const hasNoEmployeesSelected =
        employeeSelection.type === 'manual' && employeeSelection.ids.length === 0
      if (data.legalEntities?.length === 0 && hasNoEmployeesSelected && data.currency) {
        errors.strategy = i18n.t(
          'expenses_v2.settings.rate_configurations.create_modal.errors.assignees_empty'
        )
      }
      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const variables = {
        name: data.name,
        currency: data.currency,
        amountCents: data.amountCents ? toCents(Number(data.amountCents)) : undefined,
        companyId: company.id,
        legalEntityIds: !!data.legalEntities?.length ? data.legalEntities.map((le) => le.id) : [],
        employeeSelection: employeeSelection,
        rate: data.rate,
      }

      if (rateConfiguration) {
        const result = await updateRateConfiguration({
          variables: { ...variables, id: rateConfiguration.id },
        })
        const handled = handleResult(result, (errors) => {
          setErrors(errors)
        })
        handleSuccess(handled.success)

        return handled
      } else {
        const result = await createRateConfiguration({
          variables: { ...variables, resourceType: resourceType },
        })
        const handled = handleResult(result, (errors) => {
          setErrors(errors)
        })
        handleSuccess(handled.success)
        if (handled.success && handled.data) {
          if (handled.data?.rateConfiguration) {
            trackSuccessEvent(
              resourceType === ExpensableRateConfigurationResourceTypeEnum.PerDiem
                ? trackingEvents.expenses.createDiemRateConfiguration
                : trackingEvents.expenses.createMileageRateConfiguration,
              handled.data.rateConfiguration
            )
          }
        }
        return handled
      }
    },
  })

  useEffect(() => {
    if (!loadingEmployeesData && (rateConfiguration?.accessIds ?? []).length > 0) {
      const employeeIds =
        employeesData?.employees.employeeNamesConnection.nodes.map((e) => e.id) ?? []
      setEmployeeSelection({ type: 'manual', ids: employeeIds })
    }
  }, [loadingEmployeesData, employeesData, rateConfiguration?.accessIds])

  const handleSuccess = (success: boolean) => {
    if (success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })
      onClose()
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('errors.generic_saving') })
    }
  }

  const handleSelectLegalEntities = (values: LegalEntityForSelectFragment[]) => {
    const currenciesForSelector = [...new Set(values.map((legalEntity) => legalEntity.currency))]

    if (currenciesForSelector.length > 1) {
      form.setErrors({
        legalEntities: i18n.t(
          'expenses_v2.settings.rate_configurations.create_modal.errors.multiple_currencies'
        ),
      })
    } else {
      form.setErrors({ legalEntities: undefined })
    }
    form.setValues({
      ...form.values,
      currency: currenciesForSelector[0] || '',
      legalEntities: values,
    })
    setEmployeeSelection({ type: 'manual', ids: [] })
  }

  return (
    <Modal
      illustration="document-edit"
      title={title}
      description={description}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.close')}
          </Button>
          <Button onClick={() => form.handleSubmit()} disabled={form.isSubmitting}>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Form padding="s0">
        <TextField
          mandatory
          label={i18n.t('expenses_v2.settings.rate_configurations.create_modal.fields.name')}
          value={form.values.name}
          error={form.errors.name}
          onChange={(value) => form.setFieldValue('name', value, false)}
        />
        <Box flexDirection="row" gap="s16">
          {resourceType === ExpensableRateConfigurationResourceTypeEnum.PerDiem ? (
            <NumberField
              mandatory
              placeholder="0"
              indicator={form.values.currency}
              label={i18n.t('expenses_v2.settings.rate_configurations.create_modal.fields.amount')}
              value={form.values.amountCents}
              error={form.errors.amountCents}
              onChange={(value) => {
                form.setFieldValue('amountCents', value, false)
              }}
            />
          ) : (
            <NumberField
              mandatory
              placeholder={'0.0'}
              label={i18n.t('expenses_v2.settings.rate_configurations.create_modal.fields.rate')}
              value={form.values.rate}
              error={form.errors.rate}
              onChange={(value) => {
                form.setFieldValue('rate', value, false)
              }}
            />
          )}
          <SelectField
            none={i18n.t('common.none')}
            label={i18n.t('expenses_v2.settings.rate_configurations.create_modal.fields.currency')}
            mandatory
            value={form.values.currency}
            onChange={(value) => {
              form.setFieldValue('currency', value, false)
              form.setFieldValue('legalEntities', [], false)
            }}
            options={currencyOptions}
            error={form.errors.currency}
          />
        </Box>

        <FieldSet
          title={i18n.t(
            'expenses_v2.settings.rate_configurations.create_modal.fields.assignees.title'
          )}
          description={i18n.t(
            'expenses_v2.settings.rate_configurations.create_modal.fields.assignees.description'
          )}
        >
          <Radio.Wrap>
            <Radio
              name="strategy"
              value={strategyOption}
              onChange={() => {
                setStrategyOption(AccessGroupOptions.legalEntities)
                clearSelection()
              }}
              description={i18n.t(
                'expenses_v2.settings.rate_configurations.create_modal.fields.assignees.options.legal_entities.description'
              )}
              option={{
                label: i18n.t(
                  'expenses_v2.settings.rate_configurations.create_modal.fields.assignees.options.legal_entities.title'
                ),
                value: AccessGroupOptions.legalEntities,
                disabled: form.values.currency === '',
              }}
            >
              <LegalEntitySelector
                width="s400"
                multiple
                showCurrency
                defaultToggle={false}
                label={i18n.t('common.legal_entities')}
                currency={form.values.currency !== '' ? form.values.currency : undefined}
                value={form.values.legalEntities ?? []}
                onChange={handleSelectLegalEntities}
                error={form.errors.legalEntities?.toString()}
              />
            </Radio>
          </Radio.Wrap>

          <Radio.Wrap>
            <Radio
              name="strategy"
              value={strategyOption}
              onChange={() => {
                setStrategyOption(AccessGroupOptions.manualSelection)
                clearSelection()
              }}
              description={i18n.t(
                'expenses_v2.settings.rate_configurations.create_modal.fields.assignees.options.manual_selection.description'
              )}
              option={{
                label: i18n.t(
                  'expenses_v2.settings.rate_configurations.create_modal.fields.assignees.options.manual_selection.title'
                ),
                value: AccessGroupOptions.manualSelection,
                disabled: form.values.currency === '',
              }}
            >
              <EmployeeSelectorV2
                multiple
                value={employeeSelection.type === 'manual' ? employeeSelection.ids : undefined}
                onChange={(selection) => {
                  setEmployeeSelection(selection)
                  form.setFieldValue('legalEntities', [], false)
                }}
              />
            </Radio>
          </Radio.Wrap>
          {form.errors.strategy && (
            <Box alignItems="flexStart">
              <Text.Normal size="200" color="radicalRed1000" capitalize>
                {form.errors.strategy}
              </Text.Normal>
            </Box>
          )}
        </FieldSet>
      </Form>
    </Modal>
  )
}

export default RateFormModal
